import { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

const IDLE_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const idleTimer = useRef(null)

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(async () => {
      await supabase.auth.signOut()
      setUser(null)
    }, IDLE_TIMEOUT_MS)
  }, [])

  // Track user activity to reset idle timer
  useEffect(() => {
    if (!user) return

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach((e) => window.addEventListener(e, resetIdleTimer))
    resetIdleTimer()

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer))
      if (idleTimer.current) clearTimeout(idleTimer.current)
    }
  }, [user, resetIdleTimer])

  // Check for ?token= from Hub portal on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')

    if (token) {
      // Remove token from URL immediately
      window.history.replaceState({}, '', window.location.pathname)
      // Validate token with Supabase
      supabase.auth.getUser(token).then(({ data, error }) => {
        if (data?.user) {
          // Set the session using the token
          supabase.auth.setSession({
            access_token: token,
            refresh_token: '', // sub-apps don't get refresh tokens
          }).then(() => {
            setUser(data.user)
            setLoading(false)
          })
        } else {
          setLoading(false)
        }
      })
    } else {
      // Normal session check
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null)
        setLoading(false)
      })
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signOut = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    window.close()
  }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
